import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { type TProduct, useOptionalActivePageId, useProductsByIds, useSwitchToPage } from "../../../data";
import { useGrid } from "../stores";
import { graphql, readFragment } from "../../../graphql";
import { useGraphQLClient } from "@package/graphql/react";
import type { QuickAccessFragmentFragment } from "../../../__generated__/gql/graphql";
import { colParams } from "../../utils";
import { useUserId } from "../../../context/auth/hooks";

export const QuickAccessFragment = graphql(`
	fragment QuickAccessFragment on quick_access {
		id
		user_id
		page_id
		column_id
		product_id
		shortcut_number
	}
`);

export const quickAccessQuery = graphql(`
	query QuickAccessQuery($userId: String!) {
		quick_access(where: { user_id: { _eq: $userId } }) {
			...QuickAccessFragment
		}
	}
`);

// Upsert quick access. Insert if not exists, update column_id, product_id, page_id, and shortcut_number if it does.
export const quickAccessUpsertMutation = graphql(`
	mutation QuickAccessUpsertMutation($object: quick_access_insert_input!) {
		insert_quick_access_one(
			object: $object
			on_conflict: { constraint: quick_access_pkey, update_columns: [
				page_id
				column_id
				product_id
				shortcut_number
			]}
		) {
			id
		}
	}
`);

export type TQuickAccess = QuickAccessFragmentFragment;

export function useQuickAccess() {
  const switchToPage = useSwitchToPage();
  const pageId = useOptionalActivePageId();
  const userId = useUserId();
  const [api] = useGrid();
  const hasura = useGraphQLClient();

  const quickAccessData = useQuery({
    queryKey: ["quickAccess", userId],
    queryFn: async () => {
      const res = await hasura.execute(quickAccessQuery, {
        userId,
      });
      const data = readFragment(QuickAccessFragment, res.data?.quick_access);
      return data;
    },
  });
  const productIds = quickAccessData.data?.map((x) => x.product_id).filter(Boolean) || [];
  const products = useProductsByIds(productIds);

  const data =
    useMemo(() => {
      const names = getQuickAccessProductNames(quickAccessData.data ?? [], products?.data ?? []);
      return (quickAccessData.data ?? []).map((qa) => {
        return {
          ...qa,
          productName: names?.[qa.shortcut_number] || "",
        };
      });
    }, [quickAccessData.data, products.data]) || [];

  const showQuickAccessColumn = useCallback(
    ({ columnId, switchPage = false, attempt = 0 }: { columnId: string; switchPage?: boolean; attempt?: number }) => {
      if (!api) return;

      const flashDuration = switchPage ? 2000 : 500;

      const column = api.getColumns()?.find((c) => colParams(c)?.entityId === columnId);

      if (!column) {
        if (attempt > 3) {
          console.error("Failed to find column after 3 attempts");
          return;
        }

        setTimeout(() => {
          showQuickAccessColumn({
            columnId,
            switchPage,
            attempt: attempt + 1,
          });
        }, 500);
        return;
      }

      api.ensureColumnVisible(column);

      // Flash sometimes doesn't work immediately after ensuring column is visible.
      setTimeout(() => {
        api.flashCells({
          columns: [column],
          flashDuration,
          fadeDuration: flashDuration,
        });
      }, 100);
    },
    [api],
  );

  const handleQuickAccess = useCallback(
    (qa: TQuickAccess) => {
      const switchPage = pageId !== qa.page_id;

      if (switchPage) switchToPage(qa.page_id);

      showQuickAccessColumn({ columnId: qa.column_id, switchPage });
    },
    [switchToPage, pageId, showQuickAccessColumn],
  );

  return {
    data,
    handleQuickAccess,
    fetchingLocal: quickAccessData.isFetching || products.isFetching,
  };
}

function getQuickAccessProductNames(quickAccess: ReadonlyArray<TQuickAccess>, products: ReadonlyArray<TProduct>) {
  return quickAccess.reduce(
    (acc, qa) => {
      const product = products?.find((p) => p?.id === qa.product_id);
      if (!product) return acc;
      acc[qa.shortcut_number] = product.description || product.name || "";
      return acc;
    },
    {} as Record<number, string>,
  );
}
